/**
 * The Admin Panel's navigation model, and the compatibility layer that keeps old links working.
 *
 * WHY SIX DESTINATIONS, FOUR OF THEM A DELIBERATE MERGE. The panel had seven peer tabs, three of which (Overview, Usage, Analysis)
 * read the same two backends and rendered per-member spend and per-service cost three times each,
 * under six different titles. Members, Roles and Invitations are three views of one object graph, so
 * they were three peers competing for position with the tabs an admin actually visits monthly.
 *
 *   Overview        is anything wrong, and was this worth it
 *   People          who is here and what may they use   (Members · Roles · Invitations)
 *   MCP servers     which outside tools members can connect
 *   Billing         money in    — plans, card, credit, invoices
 *   Usage           money out   — one canonical per-member and per-feature surface
 *   Observability   is it healthy — agent, tool and service reliability and latency
 *
 * OBSERVABILITY IS FIFTH, AND LAST, ON PURPOSE. It arrived from the sidebar's Delivery group at
 * product's request, positioned after Usage. That ordering is also the right one: the four tabs
 * before it answer questions an admin has on a schedule (monthly bill, quarterly roster), and this
 * one answers a question they have when something is wrong. It is also the only tab whose data is
 * not org-administrative — it reads the observability event log, not the RBAC or billing surfaces —
 * so putting it anywhere earlier would interrupt the money story Billing → Usage tells in order.
 *
 * WHY THE LEGACY MAP IS NOT OPTIONAL. The active tab lives in `?tab=`, which was the whole point of
 * putting it there: an admin can send a colleague a link to the invitations list, and a refresh keeps
 * their place. Four of the old values no longer name a destination, so without this map every such
 * bookmark and every link already pasted into a chat would silently land on Overview — which looks
 * exactly like the panel losing the reader's place, the bug the URL param was added to fix.
 *
 * `rewards` is included even though its tab is currently disabled: it was a valid value, and a
 * referral reward is a credit against the bill, so Billing is where it is going when its endpoints
 * land.
 */

export type OrgAdminTab = 'overview' | 'people' | 'mcp' | 'billing' | 'usage' | 'observability';

/** The sub-tabs inside People. Each mounts an existing panel, unchanged. */
export type OrgAdminPeopleSection = 'members' | 'roles' | 'invitations';

export const ORG_ADMIN_TABS = [
  'overview',
  'people',
  'mcp',
  'billing',
  'usage',
  'observability',
] as const satisfies readonly OrgAdminTab[];

export const PEOPLE_SECTIONS = ['members', 'roles', 'invitations'] as const satisfies
  readonly OrgAdminPeopleSection[];

export const DEFAULT_TAB: OrgAdminTab = 'overview';
export const DEFAULT_PEOPLE_SECTION: OrgAdminPeopleSection = 'members';

/**
 * Where the panel currently is.
 *
 * `section` is always populated, even on tabs that have no sub-navigation, so callers never have to
 * branch on `undefined` to render the People strip.
 */
export interface AdminLocation {
  tab: OrgAdminTab;
  section: OrgAdminPeopleSection;
}

export function isOrgAdminTab(value: string | null | undefined): value is OrgAdminTab {
  return !!value && (ORG_ADMIN_TABS as readonly string[]).includes(value);
}

export function isPeopleSection(
  value: string | null | undefined,
): value is OrgAdminPeopleSection {
  return !!value && (PEOPLE_SECTIONS as readonly string[]).includes(value);
}

/**
 * Every `?tab=` value that existed before the restructure, and where it now resolves.
 *
 * The three People leaves keep their own sub-tab, so an old `?tab=roles` link opens People with
 * Roles showing — not People's default. Landing on the right section is the difference between the
 * link still working and the link merely not erroring.
 */
const LEGACY_TAB: Readonly<Record<string, AdminLocation>> = {
  members: { tab: 'people', section: 'members' },
  roles: { tab: 'people', section: 'roles' },
  invitations: { tab: 'people', section: 'invitations' },
  // Analysis merged into Usage: they were two readings of one slice of data, each carrying a link to
  // the other, sharing this very URL parameter.
  analysis: { tab: 'usage', section: DEFAULT_PEOPLE_SECTION },
  rewards: { tab: 'billing', section: DEFAULT_PEOPLE_SECTION },
};

/*
 * `/observability` is NOT listed above, and that is not an oversight.
 *
 * The legacy map translates old values of THIS parameter. `/observability` was a top-level route, so
 * a bookmark to it never carried a `?tab=` at all — it is redirected at the router instead
 * (`routes/config.ts`), which is the only thing that can keep an existing link working.
 */

/** True for a value that named a destination before the restructure and no longer does. */
export function isLegacyTabParam(value: string | null | undefined): boolean {
  return !!value && Object.prototype.hasOwnProperty.call(LEGACY_TAB, value);
}

/**
 * Resolve the URL into a location, tolerating anything.
 *
 * An unknown `tab` falls back to the default rather than rendering nothing, and an unknown `section`
 * falls back to People's default — a hand-edited or truncated URL should land somewhere sensible, not
 * on a blank panel.
 */
export function resolveAdminLocation(
  tabParam: string | null | undefined,
  sectionParam: string | null | undefined,
): AdminLocation {
  const legacy = tabParam ? LEGACY_TAB[tabParam] : undefined;
  if (legacy) return legacy;

  const tab = isOrgAdminTab(tabParam) ? tabParam : DEFAULT_TAB;
  const section = isPeopleSection(sectionParam) ? sectionParam : DEFAULT_PEOPLE_SECTION;
  return { tab, section };
}

/**
 * The canonical query parameters for a location.
 *
 * `section` is only emitted on People: carrying it everywhere would put a meaningless
 * `&section=members` on every Billing link an admin copies.
 */
export function adminLocationParams(location: AdminLocation): { tab: string; section?: string } {
  return location.tab === 'people'
    ? { tab: location.tab, section: location.section }
    : { tab: location.tab };
}
